import { ALL_VALUES_FILTER_PATH } from "containers/Settings/RuleBooks/constants";
import startCase from "lodash/startCase";

interface Property {
  name: string;
  displayName?: string;
  type?: string;
  via?: string;
}

interface PropertySet<T> extends Property {
  set?: T[];
}

interface Relation {
  id: string;
  name?: string;
  revName?: string;
  via: string;
  uniq?: string;
}

interface ManyRelation {
  id: string;
  via: string;
  name: string;
  revName?: string;
}

interface DataType {
  id: string;

  // The attribute of the data type that is used as the unique identifier
  idAttribute: string;

  // A list of many-to-many relations with other DataTypes in the schema
  manyRelations?: Array<ManyRelation>;

  // The Absolute JSON path to this data type in the JSON dataset
  path: string;

  // The name of the data type in plural form
  plural: string;

  // The possessive name for the data type, used for displaying reverse relations
  possessive: string;

  // A list of properties that directly exist on this DataType
  properties?: Array<Property | PropertySet<unknown>>;

  // A list of related DataTypes and the attributes that link them
  relations?: Relation[];

  // The name of the data type in singular form
  singular: string;
}

export interface RootDataType extends DataType {
  // The name of the data type in plural form
  plural: "";

  // The possessive name for the data type, used for displaying reverse relations
  possessive: "";

  // The name of the data type in singular form
  singular: "";

  // The attribute of the data type that is used as the unique identifier
  id: "$";

  idAttribute: "";

  // The Absolute JSON path to this data type in the JSON dataset
  path: "$";
}

export interface SchemaElement {
  dataType: LinkedDataType;
}

export interface LinkedRelation extends SchemaElement {
  id: string;
  name: string;
  via: string;
}

export interface LinkedManyRelation extends SchemaElement {
  id: string;
  via: string;
  name: string;
}

export interface LinkedProperty extends Property, SchemaElement {
  type: string;
  via: string;
}

export interface LinkedPropertySet<T> extends PropertySet<T>, SchemaElement {
  type: string;
  via: string;
}

export interface LinkedDataType
  extends SchemaElement,
    Omit<DataType, "relations" | "manyRelations"> {
  properties: Array<LinkedProperty | LinkedPropertySet<unknown>>;

  relations: LinkedRelation[];

  manyRelations: LinkedManyRelation[];
}

export interface LinkedRootDataType extends LinkedDataType {
  via: "$";
  id: "$";
  path: "$";
}

export type LinkedFieldSchema = LinkedRootDataType;

export function getRelationPath(pathPart: string) {
  // Remove the rel: and rels: prefix from the path
  return pathPart.replace(/^(rels?:)/, "");
}

export function getPropertyDisplayName(property) {
  const propertyName =
    typeof property === "object" && typeof property.name === "string"
      ? property.name
      : property || "";

  if (typeof property !== "string") {
    return "";
  }
  return startCase(
    propertyName
      .replaceAll(/_/g, " ")
      .replaceAll(/\./g, " ")
      .replaceAll(/[^A-Za-z ]/g, ""),
  );
}

function linkReverseRelation(
  sourceDataType: LinkedDataType,
  relatedDataType: LinkedDataType,
  sourceRelation: Relation,
  rootDataType: LinkedRootDataType,
) {
  const sourceRootRelation = rootDataType.manyRelations.find(
    rootManyRel => rootManyRel.id === sourceDataType.id,
  );
  relatedDataType.manyRelations.push({
    id: sourceDataType.id,
    via: `${getRelationPath(sourceRelation.via)}_${sourceRootRelation.via}`,
    name:
      sourceRelation.revName ||
      `${relatedDataType.possessive} ${sourceDataType.plural}`,
    dataType: sourceDataType,
  });
}

// Helper function to process relations and many-to-many relations
export function processRelations(
  linkedDataType: LinkedDataType,
  relations: Array<Relation | ManyRelation> | undefined,
  isManyRelation: boolean,
  rootDataType: LinkedRootDataType,
) {
  if (!relations) return; // Skip if there are no relations

  relations.forEach(relation => {
    const manyRelation = rootDataType.manyRelations.find(
      linkedManyRelation => relation.id === linkedManyRelation.id,
    );
    const relatedDataType = manyRelation.dataType; // Find the related data type in the linked schema
    const via = isManyRelation
      ? `${getRelationPath(relation.via)}${ALL_VALUES_FILTER_PATH}`
      : getRelationPath(relation.via);
    const name =
      relation.name ||
      (isManyRelation ? relatedDataType.plural : relatedDataType.singular);

    const linkedRelation = {
      id: relation.id,
      dataType: relatedDataType,
      name,
      via,
    };

    if (isManyRelation) {
      // Add to manyRelations if it's a many-to-many relation
      linkedDataType.manyRelations.push(linkedRelation as LinkedManyRelation);
    } else {
      // Otherwise, add to regular relations
      linkedDataType.relations.push(linkedRelation as LinkedRelation);
    }

    // Link the reverse relation for the related data type
    linkReverseRelation(
      linkedDataType,
      relatedDataType,
      relation,
      rootDataType,
    );
  });
}

export function linkSchema(dataTypes: DataType[]): LinkedRootDataType {
  const rootDataType: LinkedRootDataType = {
    id: "$",
    via: "$",
    idAttribute: "",
    relations: [],
    properties: [],
    manyRelations: [],
    plural: "",
    possessive: "",
    singular: "",
    path: "$",
    dataType: null,
  };
  rootDataType.dataType = rootDataType; // Set the dataType property to the rootDataType itself

  // Create a linked schema by transforming the input schema into a map of LinkedDataType objects
  rootDataType.manyRelations = dataTypes.reduce((acc, dataType) => {
    const via = getRelationPath(dataType.path);

    const linkedManyRelation: LinkedManyRelation = {
      id: dataType.id,
      via: `${via}${ALL_VALUES_FILTER_PATH}`, // Placeholder for the via property
      name: dataType.plural,
      dataType: undefined, // Placeholder for the dataType property
    };
    const linkedDataType: LinkedDataType = {
      ...dataType,
      properties: [],
      dataType: undefined, // Placeholder for the dataType property
      relations: [], // Initialize relations as an empty array
      manyRelations: [], // Initialize manyRelations as an empty array
    };

    linkedManyRelation.dataType = linkedDataType as LinkedDataType; // Set the dataType property to the linkedDataType itself
    linkedDataType.dataType = linkedDataType as LinkedDataType; // Set the dataType property to the linkedDataType itself

    dataType.properties.forEach(property =>
      linkedDataType.properties.push({
        ...property,
        name: getPropertyDisplayName(property.name),
        via: property.via ? property.via : property.name, // Use the property name or via as the identifier
        type: property.type || "string", // Set the valueType to the property type or default to string
        dataType: linkedDataType as LinkedDataType, // Set the dataType property to the linkedDataType itself
      }),
    );

    acc.push(linkedManyRelation);

    return acc;
  }, [] as LinkedManyRelation[]);

  // Iterate over the schema to process relations and many-to-many relations for each data type
  dataTypes.forEach(dataType => {
    const linkedDataType = rootDataType.manyRelations.find(
      manyRelation => manyRelation.id === dataType.id,
    ).dataType;

    // Process one-to-one and one-to-many relations
    processRelations(linkedDataType, dataType.relations, false, rootDataType);

    // Process many-to-many relations
    processRelations(
      linkedDataType,
      dataType.manyRelations,
      true,
      rootDataType,
    );
  });

  // Return the linked schema as an array of LinkedDataType objects
  return rootDataType;
}

type Attribute =
  | LinkedProperty
  | LinkedPropertySet<unknown>
  | LinkedManyRelation
  | LinkedRelation;

export function getAllAttributes(dataType: LinkedDataType): Attribute[] {
  const attrs: Attribute[] = [];
  if (!dataType) {
    return attrs;
  }

  [dataType.properties, dataType.relations, dataType.manyRelations].forEach(
    (dataTypeAttrs: Attribute[]) =>
      dataTypeAttrs.forEach((attr: Attribute) => attrs.push(attr)),
  );

  return attrs;
}
export function getRelations(dataType: LinkedDataType): LinkedRelation[] {
  if (!dataType) {
    return [];
  }
  return dataType.relations;
}

export function getManyRelations(
  dataType: LinkedDataType,
): LinkedManyRelation[] {
  if (!dataType) {
    return [];
  }
  return dataType.manyRelations;
}

export function getAllRelations(
  dataType: LinkedDataType,
): (LinkedRelation | LinkedManyRelation)[] {
  const relations: (LinkedRelation | LinkedManyRelation)[] = [];

  if (!dataType) {
    return relations;
  }

  [dataType.relations, dataType.manyRelations].forEach(
    (dataTypeRelations: (LinkedRelation | LinkedManyRelation)[]) =>
      dataTypeRelations.forEach(
        (relation: LinkedRelation | LinkedManyRelation) =>
          relations.push(relation),
      ),
  );

  return relations;
}

export function getRelation(
  dataType: LinkedDataType,
  via: string,
): LinkedRelation | null {
  const relation = getAllRelations(dataType).find(
    dataTypeRelation => dataTypeRelation.via === via,
  );

  return relation || null;
}

export function getPathParts(path: string | null): string[] {
  if (!path) {
    return [];
  }
  return path.split(".");
}

//---------------------------------------------------
// Converts an arbitrary array of strings, representing the elements of a JSON Path
// into a single string, with the elements joined by a dot.
// Returns an empty string when the array is empty.
//----------------------------------------------------
export function normalisePathParts(pathParts: string[]): string {
  if (pathParts.length === 0) {
    return "";
  }
  return pathParts.filter(Boolean).join(".");
}

export function getRelationByPath(
  dataType: LinkedDataType,
  path: string,
): LinkedRelation | LinkedManyRelation | null {
  const pathParts = getPathParts(path);
  if (pathParts.length === 0) {
    return null;
  }

  const [firstPathPart, ...remainingParts] = pathParts;
  const relation = getAllRelations(dataType).find(
    rel => rel.via === firstPathPart,
  );

  if (!relation || remainingParts.length === 0) {
    return relation || null;
  }

  return getRelationByPath(
    relation.dataType,
    normalisePathParts(remainingParts),
  );
}

export function getDataTypeByPath(
  dataType: LinkedDataType,
  path: string,
): LinkedDataType | null {
  if (dataType && path === "") {
    return dataType;
  }
  // Follow relations to the parent data type
  const relation = getRelationByPath(dataType, path);
  if (!relation) {
    return null;
  }
  return relation.dataType;
}

export function getPropertyByPath(
  dataType: LinkedDataType,
  path: string,
): LinkedProperty | LinkedPropertySet<unknown> | null {
  if (!dataType) {
    return null;
  }
  // Follow relations to the parent data type
  const pathParts = getPathParts(path);
  const propertyParthPart = pathParts.pop();
  let parentDataType = dataType;
  if (pathParts.length > 0) {
    parentDataType = getDataTypeByPath(dataType, normalisePathParts(pathParts));
  }
  if (!parentDataType) {
    return null;
  }
  // get the property from the parent data type
  return (
    parentDataType.properties.find(
      property => property.via === propertyParthPart,
    ) || null
  );
}

export function getType(dataType: LinkedDataType, path: string): string | null {
  const property = getPropertyByPath(dataType, path);
  if (!property) {
    return null;
  }
  return property.type;
}
